import IPlayerData from '../../Player/IPlayerData';
import { restoreNumber } from '../../Player/ParseHelper';
import { ICustomEntryPoint } from '../../State/Game/EntryPoint/GameEntryPoint';
import { IAPCatalog } from '../../State/Game/IAP';
import { ProductID } from '../../State/Player/Product';
import { FBTrackingEvent } from '../Tracking/FBTrackingEvent';
import { AdvertisementType, IAdvertisementPlacement } from './Advertisement';
import { CrossPromoData } from './CrossPromo';
import ISDKDriver, { IScoreEntry } from './ISDKDriver';
import { LanguageKey, isSupportedLanguage, sliceLanguage } from './Language';
import SDKManager from './SDKManager';
import { IAuthReturnValues } from '../../State/Game/Action/Login';
import Game from '../../Game';
import { LevelStatus } from '../../State/Level/ILevelState';
import { IGameState } from '../../State/Game/IGameState';
import { PlayerActions } from '../../State/Player/PlayerActions';
import { GameActions } from '../../State/Game/GameActions';
declare var sgSdk: any;

export default class SDKWEBDriver implements ISDKDriver {
    private sdkHandler: any;
    private sgSettings: any;
    private lastMusicState: number;
    public async init(sdk: SDKManager) {
        if (!window.localStorage) throw Error('Local storage not supported!');
        try {
            const oldSdkSettings: any = {
                id: 'solitaire-tripeaks-2',
                // @ts-ignore
                build: __RELEASE__,
                supportedLanguages: [
                    'en',
                    'de',
                    'es',
                    'fr',
                    'it',
                    'pt',
                    'ru',
                    'tr',
                    'nl',
                    'pl',
                    'ja',
                ],
                goToLevel: (level: number) => {
                    return;
                },
                freezeGame: this.freezeGame.bind(this),
                unfreezeGame: this.unfreezeGame.bind(this),
                runGame: this.runGame.bind(this),
            };

            const oldSdkModules = ['basic', 'levelGame'];
            await new Promise((resolve, reject) =>
                sgSdk.initialize(
                    oldSdkModules,
                    oldSdkSettings,
                    (err: any, settings: any, handler: any) => {
                        if (err) {
                            reject(err);
                            return;
                        }
                        this.sgSettings = settings;
                        this.sdkHandler = handler;
                        resolve();
                    }
                )
            );
        } catch (error) {
            const prefix: string = 'Failed to initialize sgSdk: ';
            if (error) {
                if (error.message) error.message = prefix + error.message;
                throw error;
            } else {
                throw new Error(prefix + error);
            }
        }
    }

    public loadComplete() {
        return new Promise(
            async (resolve: any, reject: any): Promise<any> => {
                this.sdkHandler.trigger('loading.completed', {
                    callback: (err: any): any => {
                        if (err) reject();
                        else resolve();
                    },
                });
            }
        );
    }

    public async loadingProgress(progress: number) {
        return;
    }

    public runGame(): void {
        Game.state.dispatch(GameActions.gameLoaded());
    }

    public freezeGame(): void {
        Game.ticker.stop();
        this.lastMusicState = Game.state.getState().playerState.volumeMusic;
        Game.state.dispatch(PlayerActions.setVolumeMusic(0));
    }

    public unfreezeGame(): void {
        Game.ticker.start();
        Game.state.dispatch(PlayerActions.setVolumeMusic(this.lastMusicState));
    }

    public async save(data: Partial<IPlayerData>) {
        Object.entries(data).forEach(([key, value]) => {
            window.localStorage.setItem(key, `${value}`);
        });
    }

    public async flushSaveData() {
        return;
    }

    public async restore(): Promise<IPlayerData> {
        return Object.keys(window.localStorage).reduce(
            (acc: any, key: string) => {
                const val = window.localStorage.getItem(key);

                if (val) {
                    const numeric = restoreNumber(val);
                    if (!isNaN(numeric) && typeof numeric === 'number') {
                        acc[key] = numeric;
                    } else if (val === 'null') {
                        acc[key] = null;
                    } else {
                        acc[key] = val;
                    }
                }
                return acc;
            },
            {}
        );
    }

    public async onStart() {
        this.sdkHandler.trigger('start');
    }

    public async runQuery() {
        return {};
    }

    public getAvatar(): string {
        return 'no_avatar';
    }

    public getUserId(): string {
        return '2103967476289832';
    }

    public getUsername(): string {
        return 'test_username';
    }

    public async getServerTime(): Promise<string> {
        return window.performance.now().toString();
    }

    public async playWithFriends(
        text: string,
        imageBase64: string,
        payload: any,
        friendId?: string
    ) {
        return 'context_id';
    }

    public async customMessage(
        text: string,
        imageBase64: string,
        payload: any,
        strategy?: string,
        cta?: string
    ) {
        return 'custom-message';
    }

    public getEntryPoint(): any {
        return;
    }

    public async purchaseProduct(productId: string) {
        return;
    }

    public async resolveUnconsumedPurchases(): Promise<ProductID[]> {
        return [];
    }

    public async fetchCatalog() {
        return;
    }

    public getCatalog(): IAPCatalog {
        return {
            products: [
                {
                    productId: ProductID.ID_007_COINS_2k,
                    price: '$ 1.99',
                    priceCurrencyCode: 'USD',
                },
                {
                    productId: ProductID.ID_008_COINS_6k,
                    price: '$ 4.99',
                    priceCurrencyCode: 'USD',
                },
                {
                    productId: ProductID.ID_009_COINS_22k,
                    price: '$ 14.99',
                    priceCurrencyCode: 'USD',
                },
                {
                    productId: ProductID.ID_010_COINS_100k,
                    price: '$ 44.99',
                    priceCurrencyCode: 'USD',
                },
                {
                    productId: ProductID.ID_011_SPECIAL_OFFER,
                    price: '$ 9.99',
                    priceCurrencyCode: 'USD',
                },
                {
                    productId: ProductID.ID_012_SPECIAL_JOKER,
                    price: '$ 19.99',
                    priceCurrencyCode: 'USD',
                },
            ],
        };
    }

    public async postScore(
        leaderboardName: string,
        instanceId: string,
        score: number
    ) {
        return 'Local Post Score.';
    }

    public async getFriendsScores(
        leaderboardName: string,
        instanceId: string
    ): Promise<IScoreEntry[]> {
        return [];
    }

    public async share(
        text: string,
        imageBase64: string,
        payload?: ICustomEntryPoint,
        intent?: string
    ) {
        return 'Test Share';
    }

    public async createContext(playerId: string) {
        return 'Test Create New Context';
    }

    public async clearPlayerData() {
        window.localStorage.clear();
    }

    public async levelStart(level: number) {
        this.sdkHandler.trigger('levelStart', { level });
    }

    public async levelFinish(state: 'win' | 'lose') {
        const {
            levelState: { levelNumber, score },
        } = Game.state.getState();

        return new Promise(callback => {
            this.sdkHandler.trigger('levelFinish', {
                score,
                state,
                callback,
                level: levelNumber,
            });
        });
    }

    public async displayAdvertisement(
        placement: IAdvertisementPlacement,
        state: IGameState,
        payload: any
    ) {
        switch (placement.type) {
            case AdvertisementType.REWARDED:
                return new Promise((resolve, reject) => {
                    this.sdkHandler.trigger(
                        'rewardedAd',
                        {
                            callback: (success: boolean) => {
                                if (success) {
                                    resolve();
                                } else {
                                    reject();
                                }
                            },
                        },
                        this
                    );
                });
            case AdvertisementType.INTERSTITIAL:
                return new Promise((resolve, reject) => {
                    this.sdkHandler.trigger(
                        'beforePlayButtonDisplay',
                        { callback: resolve },
                        this
                    );
                });
        }
    }

    public async canSubscribeBot() {
        return false;
    }

    public async subscribeBot() {
        return false;
    }

    public async canCreateShortcut() {
        return false;
    }

    public async createShortcut() {
        return;
    }

    public async gameTracking(
        event: FBTrackingEvent,
        value: string | number = null,
        parameters: { [key: string]: string | number } = null
    ) {
        return true;
    }

    public getLanguage(): LanguageKey {
        // LanguageKey.ENGLISH_US
        const lang = sliceLanguage(this.sgSettings.config.env.locale);
        return isSupportedLanguage(lang) ? lang : LanguageKey.ENGLISH_US;
    }

    public hasIAPSupport(): boolean {
        return false;
    }

    public hasRewardedAdSupport(): boolean {
        return true;
    }

    public hasInterstitialAdSupport(): boolean {
        return true;
    }

    public async switchGame(appID: string, placement: string) {
        return false;
    }

    public canSwitchGame(): boolean {
        return false;
    }

    public async getConnectedPlayerIDs(): Promise<string[]> {
        return [];
    }

    public async getLeaderboardPlayerScores(): Promise<number[]> {
        return null;
    }

    public async getCrossPromoData(): Promise<CrossPromoData> {
        return null;
    }

    public async isGBCConnected() {
        return false;
    }

    public async waitForPaymentsEnabled(): Promise<void> {
        return;
    }

    public hasCrossPromoSupport(): boolean {
        return false;
    }

    public hasShareableMomentsSupport(): boolean {
        return false;
    }

    public hasSocialFeaturesSupport(): boolean {
        return false;
    }

    public isInstallFromUpdate(): boolean {
        return false;
    }

    public openMarket(appLink: string): void {
        return;
    }

    public openUrl(url: string): void {
        return;
    }

    public sendEmail(email: string, subject: string): void {
        return;
    }

    public subscribeToNotifications(subscribe: boolean): void {
        return;
    }

    public shouldShowGDPR(): boolean {
        return false;
    }

    public signIn(silent: boolean = false): Promise<IAuthReturnValues> {
        return;
    }
    public signOut(): void {
        return;
    }

    public fetchDataConflicts(): Promise<IPlayerData[]> {
        return;
    }

    public getServicesId(): string {
        return '';
    }
}
